import axios from "axios";
import { getStaffRoleFromUser, isTeachingStaff } from "../utils/staffRoles";

/**
 * CRA injects REACT_APP_* at build time. If unset, fall back so local dev still works
 * after cloning or before restarting `npm start`.
 */
export function getApiBaseUrl() {
    const raw = process.env.REACT_APP_BASE_URL || "http://localhost:5000";
    return String(raw).replace(/\/$/, "");
}

/** Public anon key — required when REACT_APP_BASE_URL points at Supabase Edge Functions. */
export function getSupabaseAnonKey() {
    return String(process.env.REACT_APP_SUPABASE_ANON_KEY || "").trim();
}

/**
 * Local Express payment API — only used when explicitly set and reachable.
 * Ignores localhost URLs on deployed sites (Netlify, etc.).
 */
export function getPaymentApiBaseUrl() {
    const raw = String(process.env.REACT_APP_PAYMENT_API_URL || "").replace(/\/$/, "");
    if (!raw) return "";

    const isLocalPaymentApi = /^https?:\/\/(localhost|127\.0\.0\.1|\[::1\])(:\d+)?$/i.test(raw);
    if (isLocalPaymentApi && typeof window !== "undefined") {
        const host = window.location.hostname;
        if (host !== "localhost" && host !== "127.0.0.1" && host !== "[::1]") {
            return "";
        }
    }

    return raw;
}

export function isSupabaseFunctionsApi(baseUrl = getApiBaseUrl()) {
    return /supabase\.co\/functions/i.test(baseUrl);
}

/**
 * Standalone Sclass function may be undeployed (Supabase function count limit).
 * Route class detail/update calls through deployed SclassList instead.
 */
export function remapSclassApiUrl(url, baseUrl = getApiBaseUrl()) {
    if (!url || !isSupabaseFunctionsApi(baseUrl)) return url;
    const base = String(baseUrl).replace(/\/$/, "");
    if (!url.startsWith(base)) return url;

    const path = url.slice(base.length);
    const undefinedList = path.match(/^\/undefinedList\/([^/?]+)/);
    if (undefinedList) {
        return `${base}/SclassList/${encodeURIComponent(undefinedList[1])}`;
    }
    const feeAid = path.match(/^\/FeeAid(\/.*)?$/);
    if (feeAid) {
        return `${base}/FeeReports${path}`;
    }
    const students = path.match(/^\/Sclass\/Students\/([^/?]+)/);
    if (students) {
        return `${base}/SclassList/class/${students[1]}/students`;
    }
    const nextId = path.match(/^\/Sclass\/([^/]+)\/next-student-id/);
    if (nextId) {
        return `${base}/SclassList/class/${nextId[1]}/next-student-id`;
    }
    const detail = path.match(/^\/Sclass\/([^/?]+)/);
    if (detail) {
        return `${base}/SclassList/class/${detail[1]}`;
    }
    return url;
}

function subscriptionPaymentUrl(path) {
    const normalized = path.startsWith("/") ? path : `/${path}`;
    const paymentBase = getPaymentApiBaseUrl();

    if (paymentBase) {
        return `${paymentBase}${normalized}`;
    }

    const base = getApiBaseUrl();
    if (isSupabaseFunctionsApi(base)) {
        // PayChangu routes live on AdminReg (body.intent) — not Login (login-only on old deploys).
        if (
            normalized === "/SubscriptionPaymentCheckout" ||
            normalized.startsWith("/SubscriptionPaymentInitiate")
        ) {
            return `${base}/AdminReg`;
        }
        return `${base}${normalized}`;
    }

    return `${base}${normalized}`;
}

/** Build a full API URL; maps legacy Express paths to deployed Edge Function names. */
export function buildApiUrl(path) {
    const normalized = path.startsWith("/") ? path : `/${path}`;

    if (
        normalized.startsWith("/SubscriptionPaymentInitiate") ||
        normalized.startsWith("/SubscriptionPaymentCheckout")
    ) {
        return subscriptionPaymentUrl(normalized);
    }

    const base = getApiBaseUrl();
    if (!isSupabaseFunctionsApi(base)) {
        return `${base}${normalized}`;
    }

    if (
        normalized === "/Subscription/trial-extension-request" ||
        normalized === "/Subscription/payment-request" ||
        normalized === "/Subscription/activate" ||
        normalized.startsWith("/Subscription/payment-initiate") ||
        normalized.startsWith("/Subscription/payment-status/")
    ) {
        return `${base}/Subscription${normalized.slice("/Subscription".length)}`;
    }
    const subscriptionStatusMatch = /^\/Subscription\/([^/]+)$/.exec(normalized);
    if (subscriptionStatusMatch) {
        return `${base}/Admin/${encodeURIComponent(subscriptionStatusMatch[1])}`;
    }

    if (normalized.startsWith("/SchoolPublishedResultsOverview/")) {
        const rest = normalized.slice("/SchoolPublishedResultsOverview".length);
        return `${base}/SchoolResultsOverview${rest}?publishedOnly=true`;
    }
    if (normalized.startsWith("/FeeAid")) {
        return `${base}/FeeReports${normalized}`;
    }
    if (normalized.startsWith("/FeeReport/")) {
        return `${base}/FeeReports${normalized.slice("/FeeReport".length)}`;
    }
    if (normalized.startsWith("/FeeAudit/")) {
        return `${base}/FeeReports/FeeAudit${normalized.slice("/FeeAudit".length)}`;
    }
    if (normalized.startsWith("/FeeBulk/")) {
        return `${base}/FeeRecordAssign${normalized.replace("/FeeBulk", "")}`;
    }
    if (normalized.startsWith("/ExpensesExport/")) {
        return `${base}/ExpenseSummary/ExpensesExport${normalized.slice("/ExpensesExport".length)}`;
    }
    if (normalized.startsWith("/Budgets/")) {
        return `${base}/Budget${normalized.slice("/Budgets".length)}`;
    }
    if (
        normalized.startsWith("/admin/bulk-students") ||
        normalized.startsWith("/StudentBulkImport")
    ) {
        return `${base}/StudentReg${normalized}`;
    }
    if (normalized.startsWith("/TermResources/")) {
        return `${base}/TeachingResources${normalized}`;
    }
    if (normalized.startsWith("/TermBudget")) {
        return `${base}/Budget${normalized}`;
    }
    if (normalized.startsWith("/MaterialRequest")) {
        return `${base}/Budget${normalized}`;
    }
    if (normalized.startsWith("/Sclass/Students/")) {
        const classId = normalized.slice("/Sclass/Students/".length).split("/")[0];
        return `${base}/SclassList/class/${encodeURIComponent(classId)}/students`;
    }
    const sclassNextId = /^\/Sclass\/([^/]+)\/next-student-id/.exec(normalized);
    if (sclassNextId) {
        return `${base}/SclassList/class/${encodeURIComponent(sclassNextId[1])}/next-student-id`;
    }
    const sclassDetail = /^\/Sclass\/([^/]+)$/.exec(normalized);
    if (sclassDetail) {
        return `${base}/SclassList/class/${encodeURIComponent(sclassDetail[1])}`;
    }
    if (normalized.startsWith("/AcademicPeriod/")) {
        return `${base}/SchoolCalendar${normalized}`;
    }
    if (normalized.startsWith("/TermSubject/")) {
        return `${base}/TeacherSubject${normalized}`;
    }
    if (normalized.startsWith("/CaAssessment")) {
        return `${base}/MarksSubmissions${normalized}`;
    }
    if (normalized.startsWith("/StaffAttendanceRegister/")) {
        return `${base}/StaffAttendanceRegister${normalized.slice("/StaffAttendanceRegister".length)}`;
    }
    if (
        normalized.startsWith("/TeacherHr/") ||
        normalized.startsWith("/TeacherManagement/") ||
        normalized.startsWith("/TeacherQualification/") ||
        normalized.startsWith("/TeacherDeployment/") ||
        normalized.startsWith("/TeacherCpd/") ||
        normalized.startsWith("/TeacherAppraisal/") ||
        normalized.startsWith("/TeacherPayrollItem/") ||
        normalized.startsWith("/TeacherPayslip/")
    ) {
        return `${base}/Teacher${normalized}`;
    }
    if (
        normalized.startsWith("/LibraryItems/") ||
        normalized.startsWith("/LibraryItem") ||
        normalized.startsWith("/LibraryOverview/") ||
        normalized.startsWith("/LibraryBorrowers/") ||
        normalized.startsWith("/LibraryBorrowings/") ||
        normalized.startsWith("/LibraryBorrowing") ||
        normalized.startsWith("/LibraryReport/")
    ) {
        return `${base}/Library${normalized}`;
    }
    if (normalized.startsWith("/Analytics/")) {
        return `${base}/FeeReports/Analytics${normalized.slice("/Analytics".length)}`;
    }
    if (normalized.startsWith("/EmisReports/")) {
        return `${base}/FeeReports/EmisReports${normalized.slice("/EmisReports".length)}`;
    }
    if (normalized.startsWith("/Maneb/")) {
        return `${base}/FeeReports/Maneb${normalized.slice("/Maneb".length)}`;
    }
    if (normalized.startsWith("/DisciplinaryCases/")) {
        return `${base}/FeeReports/DisciplinaryCases${normalized.slice("/DisciplinaryCases".length)}`;
    }
    if (normalized.startsWith("/DisciplinaryCase")) {
        return `${base}/FeeReports/DisciplinaryCase${normalized.slice("/DisciplinaryCase".length)}`;
    }
    if (
        normalized.startsWith("/Facilities/") ||
        normalized.startsWith("/Facility") ||
        normalized.startsWith("/SchoolAssets/") ||
        normalized.startsWith("/SchoolAsset") ||
        normalized.startsWith("/MaintenanceRequests/") ||
        normalized.startsWith("/MaintenanceRequest") ||
        normalized.startsWith("/InfrastructureOverview/") ||
        normalized.startsWith("/InfrastructureReport/")
    ) {
        return `${base}/Infrastructure${normalized}`;
    }
    if (normalized === "/System/dashboard") {
        return `${base}/System/dashboard`;
    }
    if (normalized === "/System/schools") {
        return `${base}/System/schools`;
    }
    if (normalized === "/System/payments") {
        return `${base}/System/payments`;
    }
    // Password reset is routed through Login (already deployed) — avoids a separate Edge Function.
    if (
        normalized === "/PasswordReset/request" ||
        normalized === "/PasswordReset/confirm" ||
        normalized === "/PasswordReset/validate"
    ) {
        return `${base}/Login`;
    }
    const passwordResetValidate = /^\/PasswordReset\/validate\/([^/?]+)/.exec(normalized);
    if (passwordResetValidate) {
        return `${base}/Login`;
    }
    const systemSchoolMatch = /^\/System\/schools\/([^/]+)(\/subscription)?$/.exec(normalized);
    if (systemSchoolMatch) {
        const id = encodeURIComponent(systemSchoolMatch[1]);
        if (systemSchoolMatch[2]) {
            return `${base}/System/schools/${id}/subscription`;
        }
        return `${base}/System/schools/${id}`;
    }
    return `${base}${normalized}`;
}

/**
 * Optional local Express base for analytics only (not subscription payments).
 * Set REACT_APP_ANALYTICS_API_URL=http://localhost:5000 when backend is running.
 */
export function getAnalyticsApiBaseUrl() {
    const raw = String(process.env.REACT_APP_ANALYTICS_API_URL || "").replace(/\/$/, "");
    if (!raw) return "";

    const isLocal = /^https?:\/\/(localhost|127\.0\.0\.1|\[::1\])(:\d+)?$/i.test(raw);
    if (isLocal && typeof window !== "undefined") {
        const host = window.location.hostname;
        if (host !== "localhost" && host !== "127.0.0.1" && host !== "[::1]") {
            return "";
        }
    }
    return raw;
}

/**
 * Analytics URL — Supabase FeeReports/Analytics by default, or local Express when
 * REACT_APP_ANALYTICS_API_URL is set (requires `cd backend && npm start`).
 */
export function buildAnalyticsUrl(path) {
    const normalized = path.startsWith("/") ? path : `/${path}`;
    const localAnalytics = getAnalyticsApiBaseUrl();
    if (localAnalytics) {
        return `${localAnalytics}${normalized}`;
    }
    return buildApiUrl(normalized);
}

/** Generic message shown when the app cannot reach the backend (no URLs or env names). */
export const GENERIC_CONNECTION_ERROR =
    "We could not connect to the server. Check your internet connection and try again. " +
    "If the problem continues, contact your school administrator or support.";

function logUnreachableApiDetails(baseUrl, context = "api") {
    if (process.env.NODE_ENV !== "development") return;
    if (isSupabaseFunctionsApi(baseUrl)) {
        console.warn(
            `[${context}] Server unreachable. Check REACT_APP_BASE_URL and REACT_APP_SUPABASE_ANON_KEY in frontend/.env, ` +
                "restart npm start after any .env change, and confirm Edge Functions are deployed.",
            { baseUrl },
        );
        return;
    }
    console.warn(
        `[${context}] Server unreachable. Start the local backend (cd backend && npm start), confirm Postgres is running, ` +
            "and restart the frontend after changing .env.",
        { baseUrl },
    );
}

/** User-facing hint when analytics requests fail with ERR_NETWORK. */
export function formatAnalyticsUnreachableMessage() {
    const localAnalytics = getAnalyticsApiBaseUrl();
    if (localAnalytics) {
        logUnreachableApiDetails(localAnalytics, "analytics");
        if (process.env.NODE_ENV === "development") {
            console.warn(
                "[analytics] Local analytics override is set. Start the backend or remove REACT_APP_ANALYTICS_API_URL from frontend/.env.",
            );
        }
    } else {
        logUnreachableApiDetails(getApiBaseUrl(), "analytics");
    }
    return GENERIC_CONNECTION_ERROR;
}

/** User-facing hint when axios reports ERR_NETWORK / Network Error. */
export function formatApiUnreachableMessage(baseUrl = getApiBaseUrl()) {
    logUnreachableApiDetails(baseUrl);
    return GENERIC_CONNECTION_ERROR;
}

function readStoredUser() {
    try {
        const raw = localStorage.getItem("user");
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}

/** Role sent to Edge Functions for requireRole checks (includes Bursar staff). */
export function getApiCallerRole(user = readStoredUser()) {
    if (!user) return null;
    const portal = String(user.role || "").trim();
    if (portal === "Admin") return "Admin";
    if (portal === "System") return "System";
    if (portal === "Student") return "Student";
    if (getStaffRoleFromUser(user) === "Bursar") return "Bursar";
    if (getStaffRoleFromUser(user) === "Librarian") return "Librarian";
    if (portal === "Teacher" || isTeachingStaff(getStaffRoleFromUser(user))) return "Teacher";
    return portal || null;
}

function attachApiCallerContext(config) {
    const user = readStoredUser();
    const apiRole = getApiCallerRole(user);
    const callerId = user?._id || user?.id;
    const systemToken = user?.systemSessionToken;

    const next = { ...config, headers: { ...(config.headers || {}) } };
    const params = { ...(config.params || {}) };

    if (systemToken) params.systemToken = systemToken;
    if (apiRole) params.callerRole = apiRole;
    if (callerId) params.callerId = callerId;
    if (Object.keys(params).length) next.params = params;
    return next;
}

function attachSupabaseGatewayHeaders(config) {
    if (!isSupabaseFunctionsApi()) return attachApiCallerContext(config);
    const key = getSupabaseAnonKey();
    const headers = { ...(config.headers || {}) };
    if (key && !headers.Authorization && !headers.authorization) {
        headers.Authorization = `Bearer ${key}`;
    }
    const next = attachApiCallerContext({ ...config, headers });
    return next;
}

axios.interceptors.request.use((config) => {
    const next = attachSupabaseGatewayHeaders(config);
    if (typeof next.url === "string") {
        next.url = remapSclassApiUrl(next.url);
    }
    return next;
});

if (
    process.env.NODE_ENV === "development" &&
    isSupabaseFunctionsApi() &&
    !getSupabaseAnonKey()
) {
    console.warn(
        "[api] Set REACT_APP_SUPABASE_ANON_KEY in frontend/.env (Project Settings → API → anon public), then restart npm start.",
    );
}

/** Full URL for media stored in Supabase Storage (or any absolute http(s) URL). */
export function resolveUploadedMediaUrl(photoRef) {
    if (photoRef == null) return undefined;
    const t = String(photoRef).trim();
    if (!t) return undefined;
    if (/^https?:\/\//i.test(t)) return t;
    return undefined;
}
